import moment from "moment";
import * as schemaTaskLists from "~/schemas/schema.task_lists.jsx";
import * as actionTypes from "~/store/action_types";
import { getProjectIdByTaskListId, getTaskListCompletedOnById } from "~/store/selectors/selector.taskLists.jsx";
import Angie from "~/utils/angie.jsx";
import { fetch } from "~/utils/fetch.jsx";
import * as trashActions from "./actions.trash.jsx";

export const addTaskList = (projectId, attributes) => {
  return {
    types: [actionTypes.ADD_TASK_LIST_LOADING, actionTypes.ADD_TASK_LIST_SUCCESS, actionTypes.ADD_TASK_LIST_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/task-lists`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      projectId,
      attributes,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
  };
};

export const insertTaskList = (projectId, attributes) => {
  return {
    types: [
      actionTypes.INSERT_TASK_LIST_LOADING,
      actionTypes.INSERT_TASK_LIST_SUCCESS,
      actionTypes.INSERT_TASK_LIST_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/task-lists/insert`,
        method: "POST",
        body: attributes,
      }),
    payload: {
      projectId,
      attributes,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
  };
};

export const editTaskList = (projectId, taskListId, attributes) => {
  return {
    types: [actionTypes.EDIT_TASK_LIST_LOADING, actionTypes.EDIT_TASK_LIST_SUCCESS, actionTypes.EDIT_TASK_LIST_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/task-lists/${taskListId}`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      projectId,
      taskListId,
      attributes,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
  };
};

export const fetchTaskList = (projectId, taskListId) => {
  const url = `${Angie.api_url}/projects/${projectId}/task-lists/${taskListId}`;
  return {
    types: [
      actionTypes.FETCH_TASK_LIST_LOADING,
      actionTypes.FETCH_TASK_LIST_SUCCESS,
      actionTypes.FETCH_TASK_LIST_ERROR,
    ],
    callAPI: () => fetch({ url }),
    payload: {
      projectId,
      taskListId,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
    cache: { key: url },
  };
};

export const fetchTaskLists = (projectId) => {
  const url = `${Angie.api_url}/projects/${projectId}/task-lists`;
  return {
    types: [
      actionTypes.FETCH_TASK_LISTS_LOADING,
      actionTypes.FETCH_TASK_LISTS_SUCCESS,
      actionTypes.FETCH_TASK_LISTS_ERROR,
    ],
    callAPI: () => fetch({ url }),
    payload: {
      projectId,
      schema: schemaTaskLists.arrayOfTaskLists,
    },
    cache: { key: url },
  };
};

export const fetchAllTaskLists = (projectId) => {
  const url = `${Angie.api_url}/projects/${projectId}/task-lists/all`;
  return {
    types: [
      actionTypes.FETCH_TASK_LISTS_LOADING,
      actionTypes.FETCH_TASK_LISTS_SUCCESS,
      actionTypes.FETCH_TASK_LISTS_ERROR,
    ],
    callAPI: () => fetch({ url }),
    payload: {
      projectId,
      schema: schemaTaskLists.arrayOfTaskLists,
    },
    cache: { key: url },
  };
};

export const moveToTrash = (id, parentType, parentId) => {
  const objectType = `TaskList`;
  return {
    ...trashActions.moveToTrash(objectType, id),
    types: [
      actionTypes.MOVE_TASK_LIST_TO_TRASH_LOADING,
      actionTypes.MOVE_TASK_LIST_TO_TRASH_SUCCESS,
      actionTypes.MOVE_TASK_LIST_TO_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      parentType,
      parentId,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
  };
};

export const completeTaskList = (taskListId) => (dispatch, getState) => {
  const projectId = getProjectIdByTaskListId(getState(), taskListId);

  return dispatch({
    types: [
      actionTypes.COMPLETE_TASK_LIST_LOADING,
      actionTypes.COMPLETE_TASK_LIST_SUCCESS,
      actionTypes.COMPLETE_TASK_LIST_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/complete/task-list/${taskListId}`,
        method: `put`,
      }),
    payload: {
      taskListId,
      schema: {
        single: schemaTaskLists.taskList,
      },
      projectId,
      timestamp: moment().unix(),
    },
  });
};

export const reorderTaskLists = (taskListsOrder, projectId) => {
  return {
    types: [
      actionTypes.REORDER_TASK_LIST_LOADING,
      actionTypes.REORDER_TASK_LIST_SUCCESS,
      actionTypes.REORDER_TASK_LIST_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/task-lists/reorder`,
        method: `put`,
        body: taskListsOrder,
      }),
    payload: {
      taskListsOrder,
    },
  };
};

export const moveTaskListToProject = (projectId, id, attributes) => {
  return {
    types: [
      actionTypes.MOVE_TASK_LIST_TO_PROJECT_LOADING,
      actionTypes.MOVE_TASK_LIST_TO_PROJECT_SUCCESS,
      actionTypes.MOVE_TASK_LIST_TO_PROJECT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/task-lists/${id}/move-to-project`,
        method: `put`,
        body: attributes,
      }),
    payload: {
      projectId,
      taskListId: id,
      copy: attributes.copy,
      newProjectId: attributes.project_id,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
  };
};

export const duplicateTaskList = (projectId, id, name) => {
  return {
    types: [
      actionTypes.DUPLICATE_TASK_LIST_LOADING,
      actionTypes.DUPLICATE_TASK_LIST_SUCCESS,
      actionTypes.DUPLICATE_TASK_LIST_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/task-lists/${id}/duplicate`,
        method: `post`,
        body: { name },
      }),
    payload: {
      projectId,
      taskListId: id,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
  };
};

export const reopenTaskList = (taskListId) => (dispatch, getState) => {
  const timestamp = getTaskListCompletedOnById(getState(), taskListId);
  return dispatch({
    types: [
      actionTypes.REOPEN_TASK_LIST_LOADING,
      actionTypes.REOPEN_TASK_LIST_SUCCESS,
      actionTypes.REOPEN_TASK_LIST_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/open/task-list/${taskListId}`,
        method: `put`,
      }),
    payload: {
      taskListId,
      timestamp,
      schema: {
        single: schemaTaskLists.taskList,
      },
    },
  });
};

export const taskListFromEventStream = (data = {}, type) => {
  if (data.is_complete_data) {
    const { is_complete_data, ...rest } = data;
    return {
      type,
      response: rest,
      schema: schemaTaskLists.taskList,
    };
  }

  return fetchTaskList(data.project_id, data.id);
};

export const delegateTaskListFromEventStream = (data = {}, type) => {
  const { single, order } = data;
  return {
    type,
    response: single,
    order,
    schema: schemaTaskLists.taskList,
  };
};

export const insertTaskListFromEventStream = (data) => {
  return delegateTaskListFromEventStream(data, actionTypes.INSERT_TASK_LIST_FROM_EVENT_STREAM);
};

export const addTaskListFromEventStream = (data) => {
  return taskListFromEventStream(data, actionTypes.ADD_TASK_LIST_FROM_EVENT_STREAM);
};

export const updateTaskListFromEventStream = (data) => {
  return taskListFromEventStream(data, actionTypes.UPDATE_TASK_LIST_FROM_EVENT_STREAM);
};

export const taskListMovedToTrashFromEventStream = (data) => {
  return taskListFromEventStream(data, actionTypes.TASK_LIST_MOVE_TO_TRASH_FROM_EVENT_STREAM);
};

export const taskListRestoredFromTrashFromEventStream = (data) => {
  return taskListFromEventStream(data, actionTypes.TASK_LIST_RESTORED_FROM_TRASH_FROM_EVENT_STREAM);
};

export const taskListCompletedFromEventStream = (data) => {
  return taskListFromEventStream(data, actionTypes.TASK_LIST_COMPLETED_FROM_EVENT_STREAM);
};

export const taskListReopenedFromEventStream = (data) => {
  return taskListFromEventStream(data, actionTypes.TASK_LIST_REOPENED_FROM_EVENT_STREAM);
};

export const taskListReorderedFromEventStream = (data) => {
  return {
    type: actionTypes.TASK_LIST_REORDERED_FROM_EVENT_STREAM,
    response: data,
  };
};

export const taskListMovedToProjectFromEventStream = (data) => {
  return {
    type: actionTypes.TASK_LIST_MOVED_TO_PROJECT_FROM_EVENT_STREAM,
    response: data,
    schema: schemaTaskLists.taskList,
  };
};
